import React, { useEffect, useState } from 'react';
import { useNotice } from '../components/v3/shared/NoticeProvider';
import { Heading } from '../components/ui/heading';
import { fetchWithAuth } from '../utils/api';
import { FiEdit2, FiUsers, FiPlus, FiTrash } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import IconWrapper from '../components/IconWrapper';
import Modal from '../components/Modal';
import OrganizationForm from '../components/OrganizationForm';
import { Timestamp } from '../components/ui/timestamp';

// Organization interface from the API
interface Organization {
  organization_id: string;
  organization_code: string;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
}

// The first user seeded into a new org (create mode only).
interface InitialUserData {
  email: string;
  name?: string;
  role: string;
}

// Form data interface for creating/updating organizations
interface OrganizationFormData {
  organization_id?: string;
  organization_code: string;
  name: string;
  description?: string;
  initialUser?: InitialUserData;
}

// What the panel shows after POST /admin/organizations/onboard: the response
// plus the first user's name from the form, which the response does not carry.
interface OnboardResult {
  organization_name: string;
  admin_name: string;
  user_email: string;
  setup_url: string;
  email_sent: boolean;
}

const OrganizationsManagement: React.FC = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | undefined>(undefined);
  const [onboardResult, setOnboardResult] = useState<OnboardResult | null>(null);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const { show: showNotice } = useNotice();
  
  useEffect(() => {
    fetchOrganizations();
  }, []);
  
  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const data = await fetchWithAuth('/v1/auth/admin/organizations');
      setOrganizations(data);
      setError(null);
    } catch (err) {
      setError('Failed to load organizations. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateOrganization = () => {
    setSelectedOrganization(undefined);
    setOnboardResult(null);
    setCopied(false);
    setIsModalOpen(true);
  };
  
  const handleEditOrganization = (organization: Organization) => {
    setSelectedOrganization(organization);
    setIsModalOpen(true);
  };
  
  const handleSubmitOrganization = async (organizationData: OrganizationFormData) => {
    try {
      if (organizationData.organization_id) {
        // Update existing organization
        await fetchWithAuth(`/v1/auth/admin/organizations/${organizationData.organization_id}`, {
          method: 'PUT',
          body: JSON.stringify(organizationData)
        });
        setIsModalOpen(false);
        fetchOrganizations();
      } else {
        // Create a new organization AND its first user in one transactional
        // call; the response carries a setup link for that user.
        const result = await fetchWithAuth('/v1/auth/admin/organizations/onboard', {
          method: 'POST',
          body: JSON.stringify({
            organization_code: organizationData.organization_code,
            name: organizationData.name,
            description: organizationData.description,
            initial_user: organizationData.initialUser,
          })
        });
        // Swap the form for the setup-link panel instead of closing outright.
        // The org list is refreshed on dismiss (handleCloseModal), NOT here —
        // fetchOrganizations() flips the page-level `loading`/`error` state,
        // which would tear down this modal and could lose the setup link.
        setOnboardResult({
          organization_name: result.name,
          admin_name: organizationData.initialUser?.name || result.user_email,
          user_email: result.user_email,
          setup_url: result.setup_url,
          // A response without the field reads as not sent.
          email_sent: result.email_sent === true,
        });
        setCopied(false);
      }
    } catch (err) {
      throw err;
    }
  };

  const handleCopySetupUrl = async () => {
    if (!onboardResult) return;
    try {
      await navigator.clipboard.writeText(onboardResult.setup_url);
      setCopied(true);
    } catch {
      showNotice({ type: 'error', message: 'Could not copy — select and copy the link manually.' });
    }
  };

  const handleCloseModal = () => {
    // If we just onboarded an org, refresh the list now (on dismiss) so the
    // page-level loading state never tears down the setup-link panel.
    const wasOnboard = !!onboardResult;
    setIsModalOpen(false);
    setOnboardResult(null);
    setCopied(false);
    if (wasOnboard) fetchOrganizations();
  };
  
  const handleDeleteOrganization = async (organizationId: string) => {
    // Confirm deletion
    if (!window.confirm('Are you sure you want to delete this organization? This action cannot be undone.')) {
      return;
    }
    
    try {
      await fetchWithAuth(`/v1/auth/admin/organizations/${organizationId}`, {
        method: 'DELETE'
      });
      
      // Deletion was successful if we got here (no error was thrown)
      // Remove the organization from the state without needing a server refresh
      setOrganizations(currentOrgs => 
        currentOrgs.filter(org => org.organization_id !== organizationId)
      );
      
      showNotice({ type: 'success', message: 'Organization deleted successfully', autoDismissMs: 3000 });
    } catch (err: any) {
      // Check if there are users in the organization
      if (err.message && err.message.includes('Cannot delete organization with')) {
        showNotice({ type: 'warning', message: 'This organization has users assigned. Please delete or reassign these users first.' });
      } else {
        showNotice({ type: 'error', message: 'Failed to delete organization. Please try again.' });
        console.error(err);
      }
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-lg text-red-800">
        <p>{error}</p>
        <button 
          onClick={fetchOrganizations}
          className="mt-2 px-4 py-2 bg-red-100 hover:bg-red-200 rounded-md text-red-800 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }
  
  return (
    <div className="bg-white shadow-sm rounded-lg p-6 max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <Heading level={1} weight="bold" className="text-foreground">Organization Management</Heading>
          <p className="mt-1 text-sm text-gray-500">Good Outcomes team only.</p>
        </div>
        <button 
          onClick={handleCreateOrganization}
          className="px-4 py-2 bg-primary text-secondary rounded-md flex items-center hover:bg-primary/90 transition-colors"
        >
          <IconWrapper icon={FiPlus} className="mr-2" />
          Add New Organization
        </button>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="text-left border-b-2 border-gray-200">
              <th className="px-4 py-3 text-sm font-semibold text-gray-600">Name</th>
              <th className="px-4 py-3 text-sm font-semibold text-gray-600">Code</th>
              <th className="px-4 py-3 text-sm font-semibold text-gray-600">Description</th>
              <th className="px-4 py-3 text-sm font-semibold text-gray-600">Created</th>
              <th className="px-4 py-3 text-sm font-semibold text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {organizations.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-4 text-center text-gray-500">
                  No organizations found
                </td>
              </tr>
            ) : (
              organizations.map(org => (
                <tr key={org.organization_id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{org.name}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{org.organization_code}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {org.description ? org.description : <em className="text-gray-400">No description</em>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    <Timestamp value={org.created_at} format="absolute" />
                  </td>
                  <td className="px-4 py-3 flex space-x-2">
                    <button 
                      className="p-1 rounded hover:bg-gray-200 text-gray-600 transition-colors"
                      onClick={() => handleEditOrganization(org)}
                      title="Edit Organization"
                    >
                      <IconWrapper icon={FiEdit2} size={16} />
                    </button>
                    <button 
                      className="p-1 rounded hover:bg-blue-100 text-blue-600 transition-colors"
                      onClick={() => navigate(`/v4/admin/users?org=${org.organization_id}`)}
                      title="View Users"
                    >
                      <IconWrapper icon={FiUsers} size={16} />
                    </button>
                    <button 
                      className="p-1 rounded hover:bg-red-100 text-red-600 transition-colors"
                      onClick={() => handleDeleteOrganization(org.organization_id)}
                      title="Delete Organization"
                    >
                      <IconWrapper icon={FiTrash} size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Organization Form Modal — after a successful create it shows the
          initial user's setup link for the admin to copy and send. */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={
          onboardResult
            ? 'Organization created'
            : selectedOrganization
            ? 'Edit Organization'
            : 'Create Organization'
        }
      >
        {onboardResult && (
          <p className="text-sm text-gray-700">
            <span className="font-medium">{onboardResult.organization_name}</span> is set up
            and <span className="font-medium">{onboardResult.admin_name}</span> is its first user.
          </p>
        )}
        {/* Mounted with the form, before the result, so a screen reader announces the line it fills. */}
        <div role="status" className={onboardResult ? 'mt-4' : undefined}>
          {onboardResult &&
            (onboardResult.email_sent ? (
              <div className="flex items-start gap-2 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                <CheckCircleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
                <span>We've emailed {onboardResult.user_email} a link to set their password.</span>
              </div>
            ) : (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
                <span>The email didn't send. Copy the setup link and send it to them yourself.</span>
              </div>
            ))}
        </div>
        {onboardResult ? (
          <div className="mt-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Setup link</label>
              <div className="flex items-stretch gap-2">
                <input
                  type="text"
                  readOnly
                  value={onboardResult.setup_url}
                  onFocus={(e) => e.target.select()}
                  className="flex-1 p-2 border border-gray-300 rounded-md bg-gray-50 text-sm text-gray-700"
                />
                <button
                  type="button"
                  onClick={handleCopySetupUrl}
                  className="px-4 py-2 bg-primary text-secondary rounded-md hover:bg-primary/90 whitespace-nowrap"
                >
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                This link lets them set their own password. It expires in 24 hours.
              </p>
            </div>
            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={handleCloseModal}
                className="px-4 py-2 bg-primary text-secondary rounded-md hover:bg-primary/90"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <OrganizationForm
            organization={selectedOrganization}
            onSubmit={handleSubmitOrganization}
            onCancel={handleCloseModal}
          />
        )}
      </Modal>
    </div>
  );
};

export default OrganizationsManagement;
